type PresenceStatusMessage = {
  type?: string;
  status?: "detected" | "cleared";
  session_id?: string;
  device_id?: string;
  tab_id?: string;
  connected_at?: number;
  detected_at?: number;
};

type GamePresenceConfig = {
  tokenUrl: string | null;
  allowedOrigins: string[];
  targetOrigin: string | null;
};

type PresenceEnvelope = {
  messageType: "RX36_MULTI_TAB_DETECTED" | "RX36_MULTI_TAB_CLEARED";
  payload: Record<string, unknown>;
};

const PRESENCE_INIT_KEY = "__rx36PresenceInitialized";
const PRESENCE_DEBUG =
  (typeof window !== "undefined" && window.location.hostname === "localhost") ||
  (typeof localStorage !== "undefined" && localStorage.getItem("rx36_presence_debug") === "1");

const redactPresencePayload = (payload: Record<string, unknown>): Record<string, unknown> => {
  const next = { ...payload };
  if ("session_id" in next) next.session_id = "[redacted]";
  if ("device_id" in next) next.device_id = "[redacted]";
  if ("tab_id" in next) next.tab_id = "[redacted]";
  return next;
};

const logPresence = (message: string, details?: Record<string, unknown>): void => {
  if (!PRESENCE_DEBUG) {
    return;
  }
  if (details) {
    console.info(`[rx36-presence] ${message}`, details);
    return;
  }
  console.info(`[rx36-presence] ${message}`);
};

const parseOrigins = (csv: string | null): string[] => {
  if (!csv) return [];
  return csv
    .split(",")
    .map((value) => value.trim())
    .filter(Boolean);
};

const originFromUrl = (url: string | null): string | null => {
  if (!url) return null;
  try {
    return new URL(url, window.location.href).origin;
  } catch {
    return null;
  }
};

const getOrCreateId = (storage: Storage, key: string): string => {
  const existing = storage.getItem(key);
  if (existing) {
    return existing;
  }
  const value = crypto.randomUUID();
  storage.setItem(key, value);
  return value;
};

const createFreshTabId = (): string => {
  const value = crypto.randomUUID();
  sessionStorage.setItem("rx36_tab_id", value);
  return value;
};

const iframes = Array.from(document.querySelectorAll<HTMLIFrameElement>("iframe[data-rx36-game-embed]"));

if (!iframes.length) {
  logPresence("no iframes found");
}

if (iframes.length && !(window as Window & { [PRESENCE_INIT_KEY]?: boolean })[PRESENCE_INIT_KEY]) {
  (window as Window & { [PRESENCE_INIT_KEY]?: boolean })[PRESENCE_INIT_KEY] = true;
  logPresence("init", {
    origin: window.location.origin,
    iframeCount: iframes.length,
  });

  const iframeMeta = new Map<HTMLIFrameElement, GamePresenceConfig>();

  iframes.forEach((iframe) => {
    const tokenUrl = iframe.getAttribute("data-rx36-token-url");
    const allowedCsv = iframe.getAttribute("data-rx36-allowed-origins") || "";
    const allowed = parseOrigins(allowedCsv);
    const targetOrigin = originFromUrl(iframe.getAttribute("src"));
    iframeMeta.set(iframe, { tokenUrl, allowedOrigins: allowed, targetOrigin });
  });

  const deviceId = getOrCreateId(localStorage, "rx36_device_id");
  const tabId = createFreshTabId();
  const tabConnectedAt = Date.now();
  let localMultiTabDetected = false;

  const scheme = window.location.protocol === "https:" ? "wss" : "ws";
  const socketUrl = `${scheme}://${window.location.host}/ws/game-presence/`;
  let socket: WebSocket | null = null;
  let reconnectTimer: number | null = null;
  let reconnectAttempt = 0;

  const notifyIframes = (messageType: string, payload: Record<string, unknown>) => {
    logPresence("notify", {
      messageType,
      payload: redactPresencePayload(payload),
    });
    (window as Window & { __rx36PresenceStatus?: PresenceEnvelope }).__rx36PresenceStatus = {
      messageType: messageType as PresenceEnvelope["messageType"],
      payload,
    };
    for (const iframe of iframes) {
      const meta = iframeMeta.get(iframe);
      if (!meta) continue;

      const targetOrigin = meta.targetOrigin;
      if (!targetOrigin) continue;
      if (
        targetOrigin !== window.location.origin
        && meta.allowedOrigins.length
        && !meta.allowedOrigins.includes(targetOrigin)
      ) {
        continue;
      }

      logPresence("postMessage", {
        messageType,
        targetOrigin,
        allowedOrigins: meta.allowedOrigins,
      });
      iframe.contentWindow?.postMessage(
        {
          type: messageType,
          ...payload,
        },
        targetOrigin,
      );
    }
  };

  if ("BroadcastChannel" in window) {
    const channel = new BroadcastChannel("rx36_tab_presence");

    const broadcastJoin = () => {
      channel.postMessage({
        type: "tab_join",
        tab_id: tabId,
        connected_at: tabConnectedAt,
      });
    };

    const broadcastLeave = () => {
      channel.postMessage({
        type: "tab_leave",
        tab_id: tabId,
        connected_at: tabConnectedAt,
      });
    };

    channel.addEventListener("message", (event) => {
      const data = event.data as {
        type?: string;
        tab_id?: string;
        connected_at?: number;
      };
      if (!data || typeof data !== "object") return;
      if (typeof data.tab_id !== "string" || typeof data.connected_at !== "number") return;
      if (data.tab_id === tabId) return;

      logPresence("bc message", {
        type: data.type,
        connected_at: data.connected_at,
      });

      if (data.type === "tab_join") {
        if (data.connected_at > tabConnectedAt) {
          localMultiTabDetected = true;
          notifyIframes("RX36_MULTI_TAB_DETECTED", {
            tab_id: data.tab_id,
            connected_at: data.connected_at,
            detected_at: Date.now(),
          });
        }
        return;
      }

      if (data.type === "tab_leave") {
        if (data.connected_at > tabConnectedAt) {
          localMultiTabDetected = false;
          notifyIframes("RX36_MULTI_TAB_CLEARED", {
            tab_id: data.tab_id,
            connected_at: data.connected_at,
            detected_at: null,
          });
        }
      }
    });

    broadcastJoin();
    window.addEventListener("beforeunload", broadcastLeave);
  }

  const storageKey = "rx36_tab_presence_event";
  const broadcastStorageEvent = (type: "tab_join" | "tab_leave") => {
    const payload = {
      type,
      tab_id: tabId,
      connected_at: tabConnectedAt,
      ts: Date.now(),
    };
    try {
      localStorage.setItem(storageKey, JSON.stringify(payload));
    } catch {
      // Ignore storage failures.
    }
  };

  window.addEventListener("storage", (event) => {
    if (event.key !== storageKey || !event.newValue) return;
    let data: { type?: string; tab_id?: string; connected_at?: number } | null = null;
    try {
      data = JSON.parse(event.newValue) as {
        type?: string;
        tab_id?: string;
        connected_at?: number;
      };
    } catch {
      return;
    }
    if (!data || typeof data.tab_id !== "string" || typeof data.connected_at !== "number") return;
    if (data.tab_id === tabId) return;

    logPresence("storage message", {
      type: data.type,
      connected_at: data.connected_at,
    });

    if (data.type === "tab_join") {
      if (data.connected_at > tabConnectedAt) {
        localMultiTabDetected = true;
        notifyIframes("RX36_MULTI_TAB_DETECTED", {
          tab_id: data.tab_id,
          connected_at: data.connected_at,
          detected_at: Date.now(),
        });
      }
      return;
    }

    if (data.type === "tab_leave") {
      if (data.connected_at > tabConnectedAt) {
        localMultiTabDetected = false;
        notifyIframes("RX36_MULTI_TAB_CLEARED", {
          tab_id: data.tab_id,
          connected_at: data.connected_at,
          detected_at: null,
        });
      }
    }
  });

  broadcastStorageEvent("tab_join");
  window.addEventListener("beforeunload", () => broadcastStorageEvent("tab_leave"));

  const onMessage = (event: MessageEvent) => {
    let data: PresenceStatusMessage;
    try {
      data = JSON.parse(event.data) as PresenceStatusMessage;
    } catch {
      return;
    }

    logPresence("ws message", {
      type: data.type,
      status: data.status,
    });

    if (data.type !== "presence_status" || !data.status) {
      return;
    }

    if (localMultiTabDetected && data.status === "cleared") {
      logPresence("ignore ws cleared (local detected)");
      return;
    }

    const messageType =
      data.status === "detected" ? "RX36_MULTI_TAB_DETECTED" : "RX36_MULTI_TAB_CLEARED";

    notifyIframes(messageType, {
      session_id: data.session_id,
      device_id: data.device_id,
      tab_id: data.tab_id,
      connected_at: data.connected_at,
      detected_at: data.detected_at,
    });
  };

  const scheduleReconnect = () => {
    if (reconnectTimer !== null) {
      return;
    }
    reconnectAttempt = Math.min(reconnectAttempt + 1, 5);
    const delayMs = Math.min(10_000, 500 * Math.pow(2, reconnectAttempt));
    reconnectTimer = window.setTimeout(() => {
      reconnectTimer = null;
      connectSocket();
    }, delayMs);
  };

  const connectSocket = () => {
    if (socket && (socket.readyState === WebSocket.OPEN || socket.readyState === WebSocket.CONNECTING)) {
      return;
    }

    socket = new WebSocket(socketUrl);
    socket.addEventListener("open", () => {
      logPresence("ws open");
      reconnectAttempt = 0;
      socket?.send(
        JSON.stringify({
          type: "presence_join",
          tab_id: tabId,
          device_id: deviceId,
        }),
      );
    });

    socket.addEventListener("message", onMessage);
    socket.addEventListener("close", () => {
      logPresence("ws close");
      scheduleReconnect();
    });
    socket.addEventListener("error", () => {
      logPresence("ws error");
      scheduleReconnect();
    });
  };

  connectSocket();

  window.addEventListener("pageshow", () => connectSocket());
  window.addEventListener("visibilitychange", () => {
    if (document.visibilityState === "visible") {
      connectSocket();
    }
  });
}
